const AUDIT_SCRIPT_URL = 'https://analytics.cdntaptasty.com/analytics.js'
const AUDIT_ENDPOINT = 'https://analytics.cdntaptasty.com/api/audit.php'
const SYNC_INTERVAL_MS = 30000

let scriptLoadPromise = null
let initPromise = null
const pendingEvents = []

function getSessionUser (vm) {
  try {
    const raw = vm.$session && vm.$session.get('userData')
    if (!raw) return {}
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch (e) {
    return {}
  }
}

/**
 * Analytics API rejects events with location_id = null.
 * Prefer a real location true_id. Product locations are often tokens — map token → true_id.
 * Company-scoped entities (categories, etc.) use the first location from the company list.
 */
function getCompanyLocations (vm) {
  try {
    const companyData = vm.$session && vm.$session.get('selectedCompanyData')
    const details = companyData && companyData.locations_details
    if (Array.isArray(details) && details.length) {
      return details
    }
  } catch (e) {}

  try {
    const raw = vm.$session && vm.$session.get('userDataLocations')
    const list = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (Array.isArray(list) && list.length) {
      return list
    }
  } catch (e) {}

  return []
}

function locationTrueId (loc) {
  if (!loc || typeof loc !== 'object') return null
  if (loc.true_id != null && loc.true_id !== '') return loc.true_id
  // Some session lists only expose numeric id as true id
  if (loc.id != null && typeof loc.id === 'number') return loc.id
  return null
}

function mapTokenOrIdToTrueId (locations, value) {
  if (value == null || value === '') return null
  const str = String(value)

  for (let i = 0; i < locations.length; i++) {
    const loc = locations[i]
    if (!loc) continue
    if (loc.true_id != null && String(loc.true_id) === str) {
      return loc.true_id
    }
    if (loc.token != null && String(loc.token) === str) {
      return locationTrueId(loc)
    }
    // Product location_ids often store token in `.id` — never return the token itself
    if (loc.id != null && String(loc.id) === str) {
      return locationTrueId(loc)
    }
  }

  // Already a numeric true_id not in the list
  const asNum = Number(value)
  if (!isNaN(asNum) && String(asNum) === str && Number.isFinite(asNum)) {
    return asNum
  }
  return null
}

function firstPreferredValue (preferred) {
  if (preferred == null || preferred === '') return null
  if (Array.isArray(preferred)) {
    for (let i = 0; i < preferred.length; i++) {
      const item = preferred[i]
      if (item == null || item === '') continue
      if (typeof item === 'object') {
        if (item.true_id != null) return item.true_id
        if (item.token != null) return item.token
        if (item.id != null) return item.id
        continue
      }
      return item
    }
    return null
  }
  if (typeof preferred === 'object') {
    if (preferred.true_id != null) return preferred.true_id
    if (preferred.token != null) return preferred.token
    if (preferred.id != null) return preferred.id
    return null
  }
  return preferred
}

export function resolveLocationId (vm, preferred) {
  const locations = getCompanyLocations(vm)
  const candidate = firstPreferredValue(preferred)

  if (candidate != null && candidate !== '') {
    const mapped = mapTokenOrIdToTrueId(locations, candidate)
    if (mapped != null) return mapped
  }

  // Company-scoped entities (category, company details, …): first location in the list
  if (locations.length) {
    return locationTrueId(locations[0])
  }

  return null
}

export function getAuditActor (vm) {
  const user = getSessionUser(vm)
  const first = user.firstname || user.first_name || ''
  const last = user.lastname || user.last_name || ''
  const userName = `${first} ${last}`.trim() || user.email || null
  return {
    user_id: user.id || null,
    user_name: userName,
    company_id: (vm.$session && vm.$session.get('selectedCompany')) || null
  }
}

export function loadAuditScript () {
  if (typeof window === 'undefined') {
    return Promise.resolve()
  }
  if (window.AuditClient) {
    return Promise.resolve()
  }
  if (scriptLoadPromise) {
    return scriptLoadPromise
  }
  scriptLoadPromise = new Promise((resolve) => {
    const existing = document.querySelector('script[data-taptasty-audit]')
    if (existing) {
      if (window.AuditClient) {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => resolve())
      setTimeout(() => resolve(), 0)
      return
    }
    const script = document.createElement('script')
    script.src = AUDIT_SCRIPT_URL + '?t=' + Date.now()
    script.async = true
    script.dataset.taptastyAudit = '1'
    script.onload = () => resolve()
    script.onerror = () => {
      console.error('Eroare analytics: nu s-a putut încărca analytics.js')
      resolve()
    }
    document.head.appendChild(script)
  })
  return scriptLoadPromise
}

function flushPendingEvents () {
  if (!window.audit || typeof window.audit.event !== 'function') {
    return
  }
  while (pendingEvents.length) {
    const item = pendingEvents.shift()
    try {
      emitAuditEvent(item)
    } catch (err) {
      console.error('Eroare analytics ' + item.eventName, err)
    }
  }
}

function emitAuditEvent (item) {
  if (item.companyId && window.audit.companyId !== item.companyId) {
    window.audit.companyId = item.companyId
  }
  if (item.locationId != null) {
    window.audit.locationId = item.locationId
  }
  window.audit.event(
    item.eventName,
    item.entityType,
    item.entityId,
    item.payload,
    item.context
  )
}

export async function initAuditClient (vm, options = {}) {
  if (initPromise) {
    return initPromise
  }
  initPromise = (async () => {
    try {
      if (!vm || !vm.$session) {
        return null
      }
      const hasSession = typeof vm.$session.exists === 'function'
        ? vm.$session.exists()
        : !!vm.$session.get('token')
      if (!hasSession) {
        return null
      }

      await loadAuditScript()
      if (typeof window.AuditClient !== 'function') {
        console.error('Eroare analytics: AuditClient lipsește după încărcarea scriptului')
        return null
      }

      const actor = getAuditActor(vm)
      const locationId = resolveLocationId(vm, options.locationId)
      if (!actor.company_id) {
        console.warn('Analytics init: selectedCompany lipsește')
      }
      if (locationId == null) {
        console.warn('Analytics init: nu s-a găsit location_id (API respinge location_id=null)')
      }

      const version = (options.version != null)
        ? options.version
        : (process.env.APP_VERSION || '1.1.0')

      window.audit = new window.AuditClient({
        endpoint: AUDIT_ENDPOINT,
        companyId: actor.company_id,
        locationId: locationId,
        posId: options.posId != null ? options.posId : null,
        userId: actor.user_id,
        appId: null,
        version: version,
        syncInterval: SYNC_INTERVAL_MS
      })

      flushPendingEvents()
      return window.audit
    } catch (err) {
      console.error('Eroare analytics init', err)
      return null
    } finally {
      initPromise = null
    }
  })()
  return initPromise
}

function sanitizeForAudit (value, depth, seen) {
  const maxDepth = 6
  const maxString = 2000
  const maxArray = 50
  const maxKeys = 80
  if (depth == null) depth = 0
  if (!seen) seen = []

  if (value == null) return value
  const t = typeof value
  if (t === 'string') {
    return value.length > maxString ? (value.slice(0, maxString) + '…[truncated]') : value
  }
  if (t === 'number' || t === 'boolean') return value
  if (t === 'function' || t === 'symbol' || t === 'undefined') return undefined
  if (t !== 'object') return String(value)

  // File / Blob / HTMLElement etc.
  if (typeof File !== 'undefined' && value instanceof File) {
    return { __file: value.name, size: value.size, type: value.type }
  }
  if (typeof Blob !== 'undefined' && value instanceof Blob) {
    return { __blob: true, size: value.size, type: value.type }
  }

  for (let i = 0; i < seen.length; i++) {
    if (seen[i] === value) return '[Circular]'
  }
  if (depth >= maxDepth) return '[MaxDepth]'

  seen.push(value)

  if (Array.isArray(value)) {
    const out = []
    const len = Math.min(value.length, maxArray)
    for (let i = 0; i < len; i++) {
      const item = sanitizeForAudit(value[i], depth + 1, seen)
      if (item !== undefined) out.push(item)
    }
    if (value.length > maxArray) out.push('…[+' + (value.length - maxArray) + ' items]')
    seen.pop()
    return out
  }

  const outObj = {}
  const keys = Object.keys(value)
  const keyLen = Math.min(keys.length, maxKeys)
  for (let i = 0; i < keyLen; i++) {
    const key = keys[i]
    if (key === '__ob__' || key.charAt(0) === '$') continue
    try {
      const item = sanitizeForAudit(value[key], depth + 1, seen)
      if (item !== undefined) outObj[key] = item
    } catch (e) {
      outObj[key] = '[Unreadable]'
    }
  }
  if (keys.length > maxKeys) outObj.__truncated_keys = keys.length - maxKeys
  seen.pop()
  return outObj
}

function buildEventArgs (vm, eventName, entityType, entityId, payload) {
  const actor = getAuditActor(vm)
  const rawPayload = payload || {}
  const preferred =
    rawPayload.location_id != null ? rawPayload.location_id
      : (rawPayload.locationId != null ? rawPayload.locationId
        : (rawPayload.location_tokens != null ? rawPayload.location_tokens
          : (rawPayload.location_ids != null ? rawPayload.location_ids : null)))
  const locationId = resolveLocationId(vm, preferred)
  const safePayload = sanitizeForAudit(rawPayload) || {}
  const mergedPayload = Object.assign({}, safePayload, {
    user_id: actor.user_id,
    user_name: actor.user_name,
    company_id: actor.company_id,
    location_id: locationId
  })
  const context = {
    user_id: actor.user_id,
    user_name: actor.user_name,
    company_id: actor.company_id,
    location_id: locationId
  }
  return {
    eventName: eventName,
    entityType: entityType,
    entityId: entityId,
    payload: mergedPayload,
    context: context,
    companyId: actor.company_id,
    locationId: locationId
  }
}

export function trackAdminEvent (vm, eventName, entityType, entityId, payload = {}) {
  try {
    const args = buildEventArgs(vm, eventName, entityType, entityId, payload)

    if (args.locationId == null) {
      console.warn('Analytics: event skipped — location_id lipsă (API respinge null)', eventName)
      return
    }

    // Final guard: payload must be JSON-serializable (AuditClient drops non-serializable silently)
    try {
      JSON.stringify(args.payload)
    } catch (serializeErr) {
      console.error('Analytics: payload neserializabil, se trimite varianta redusă', eventName, serializeErr)
      args.payload = {
        user_id: args.payload.user_id,
        user_name: args.payload.user_name,
        company_id: args.payload.company_id,
        location_id: args.payload.location_id,
        entity_id: entityId,
        note: 'payload_stripped_unserializable'
      }
    }

    if (window.audit && typeof window.audit.event === 'function') {
      emitAuditEvent(args)
      return
    }

    pendingEvents.push(args)
    initAuditClient(vm, { locationId: args.locationId }).then((client) => {
      if (!client) {
        console.warn('Analytics: eventul a fost pus în coadă dar clientul nu s-a inițializat', eventName)
      }
    })
  } catch (err) {
    console.error('Eroare analytics ' + eventName, err)
  }
}

export function tableEntityKind (vm, modelType) {
  if (modelType === 'Kiosk') return 'kiosk'
  const path = (vm.$route && vm.$route.path)
    ? vm.$route.path
    : (typeof window !== 'undefined' ? window.location.pathname : '')
  if (String(path).toLowerCase().includes('qr')) return 'qr'
  return 'table'
}



// WEBPACK FOOTER //
// ./src/services/audit.js